/**
 * Docs → Note offers (#1037, #1044, #1046, #1049).
 * Propose from Docs. Accept / edit / Skip. No silent fill.
 * Same door as Characters. Extends kinds — not a second brain.
 * Book door defaults to this book. Series door defaults to series.
 * Logline / Theme / Conflict / Relationship / Subplot land in Book Notes.
 * Custom follows the door (Book Notes or Series Notes).
 * Story Rules land in the Story Rules store. Voice lands in Style Notes
 * as a saved draft — not forced as the book default.
 */

import { documentImportApi } from '../api/documentImport';
import { docListDetail } from './docListDetail';
import { seriesSnapshotsApi } from '../api/seriesSnapshots';
import { storyBibleApi } from '../api/storyBible';
import { storyRulesApi } from '../api/storyRules';
import { styleCardsApi } from '../api/styleCards';
import { newCardId, upsertCard } from './seriesSnapshots';
import { unwrapImportBatches } from './seriesOffers';
import {
  isBookDocsOffersPath,
  isDocsOffersPath,
  isProjectDocsOffersPath,
} from './seriesNotes';
import {
  ACCEPT_CHARACTER_NOTE_BOOK,
  ACCEPT_CHARACTER_NOTE_SERIES,
  ACCEPT_CONFLICT_NOTE_BOOK,
  ACCEPT_CUSTOM_NOTE_BOOK,
  ACCEPT_CUSTOM_NOTE_SERIES,
  ACCEPT_LOGLINE_NOTE_BOOK,
  ACCEPT_PLACE_NOTE_BOOK,
  ACCEPT_PLACE_NOTE_SERIES,
  ACCEPT_RELATIONSHIP_NOTE_BOOK,
  ACCEPT_STORY_RULE_BOOK,
  ACCEPT_SUBPLOT_NOTE_BOOK,
  ACCEPT_THEME_NOTE_BOOK,
  ACCEPT_VOICE_STYLE_BOOK,
  ACCEPT_WORLD_NOTE_BOOK,
  ACCEPT_WORLD_NOTE_SERIES,
  CHARACTER_NOTE,
  CONFLICT_NOTE,
  CUSTOM_NOTE,
  EMPTY_DOCS_FOR_NOTES,
  EXISTING_CHARACTER_NOTES,
  EXISTING_PLACE_NOTES,
  EXISTING_STORY_RULES,
  EXISTING_VOICES,
  EXISTING_WORLD_NOTES,
  FROM_DOCS_LEDE,
  LOGLINE_NOTE,
  LOOKS_LIKE_EXISTING_CONFLICT_NOTE,
  LOOKS_LIKE_EXISTING_CUSTOM_NOTE,
  LOOKS_LIKE_EXISTING_LOGLINE_NOTE,
  LOOKS_LIKE_EXISTING_NOTE,
  LOOKS_LIKE_EXISTING_PLACE_NOTE,
  LOOKS_LIKE_EXISTING_RELATIONSHIP_NOTE,
  LOOKS_LIKE_EXISTING_STORY_RULE,
  LOOKS_LIKE_EXISTING_SUBPLOT_NOTE,
  LOOKS_LIKE_EXISTING_THEME_NOTE,
  LOOKS_LIKE_EXISTING_VOICE,
  LOOKS_LIKE_EXISTING_WORLD_NOTE,
  MAKE_NOTES_FROM_DOCS,
  NO_BOOK_FOR_CHARACTER_NOTE,
  NO_MATCH_IN_DOCS,
  NO_PEOPLE_IN_DOCS,
  PLACE_NOTE,
  RELATIONSHIP_NOTE,
  REVIEW_DOCS_CHARACTER_LEDE,
  STORY_RULES,
  SUBPLOT_NOTE,
  THEME_NOTE,
  VOICE_DRAFT,
  GIVE_VOICE_ITS_TEXT,
  WORLD_NOTE,
} from './writerCopy';

export const FROM_DOCS = MAKE_NOTES_FROM_DOCS;
export const CHECKLIST_LEDE = FROM_DOCS_LEDE;
export const REVIEW_LEDE = REVIEW_DOCS_CHARACTER_LEDE;
export const EMPTY_DOCS = EMPTY_DOCS_FOR_NOTES;
export const NO_PEOPLE = NO_PEOPLE_IN_DOCS;
export const NO_MATCH = NO_MATCH_IN_DOCS;
export const EXISTING_LEDE = EXISTING_CHARACTER_NOTES;
export const LOOKS_LIKE_LEDE = LOOKS_LIKE_EXISTING_NOTE;

export const REVIEW_GROUP_ORDER = [
  'people', 'place', 'world',
  'logline', 'theme', 'conflict', 'relationship', 'subplot', 'custom',
  'rule', 'voice',
];

export const KIND_HEADING = {
  people: CHARACTER_NOTE,
  place: PLACE_NOTE,
  world: WORLD_NOTE,
  logline: LOGLINE_NOTE,
  theme: THEME_NOTE,
  conflict: CONFLICT_NOTE,
  relationship: RELATIONSHIP_NOTE,
  subplot: SUBPLOT_NOTE,
  custom: CUSTOM_NOTE,
  rule: STORY_RULES,
  voice: VOICE_DRAFT,
};

const BOOK_DEST = {
  people: { category: 'Character', tier: 'tier_1', instanceName: true },
  place: { category: 'Setting/Location', tier: 'tier_2', instanceName: true },
  world: { category: 'World', tier: 'tier_1', instanceName: false, defaultTitle: 'World' },
  logline: { category: 'Plot', tier: 'tier_1', instanceName: false, defaultTitle: 'Logline' },
  theme: { category: 'Theme', tier: 'tier_1', instanceName: false, defaultTitle: 'Theme' },
  conflict: { category: 'Conflict', tier: 'tier_1', instanceName: false, defaultTitle: 'Conflict' },
  relationship: { category: 'Relationship', tier: 'tier_2', instanceName: true },
  subplot: { category: 'Subplot', tier: 'tier_2', instanceName: true },
  custom: { category: null, tier: 'custom', instanceName: true },
};

const BOOK_ONLY_KINDS = new Set([
  'logline', 'theme', 'conflict', 'relationship', 'subplot',
  'rule', 'voice',
]);

const LABELED_DEFAULTS = {
  relationship: 'Relationship',
  subplot: 'Subplot',
  rule: 'Story Rule',
  voice: 'Style',
};

const SERIES_STORE_KINDS = new Set(['people', 'place', 'world', 'custom']);

const SINGLETON_DEFAULTS = {
  world: 'World',
  logline: 'Logline',
  theme: 'Theme',
  conflict: 'Conflict',
};

export function offerKind(kind) {
  const raw = String(kind || '').trim().toLowerCase();
  if (raw === 'character' || raw === 'person' || raw === 'people') return 'people';
  if (raw === 'place' || raw === 'setting' || raw === 'location' || raw === 'setting/location') {
    return 'place';
  }
  if (raw === 'world') return 'world';
  if (raw === 'plot' || raw === 'plot-theme' || raw === 'plot_theme' || raw === 'plot theme') {
    return 'logline';
  }
  if (raw === 'logline' || raw === 'theme' || raw === 'conflict'
    || raw === 'relationship' || raw === 'subplot' || raw === 'custom') {
    return raw;
  }
  if (raw === 'rule' || raw === 'rules' || raw === 'hard rule' || raw === 'hard rules'
    || raw === 'hard_rule' || raw === 'story rule' || raw === 'story rules'
    || raw === 'story_rule' || raw === 'story_rules') {
    return 'rule';
  }
  if (raw === 'voice' || raw === 'style' || raw === 'style note' || raw === 'style notes'
    || raw === 'style_voice' || raw === 'voice draft') {
    return 'voice';
  }
  return raw || 'people';
}

export function isBookOnlyKind(kind) {
  return BOOK_ONLY_KINDS.has(offerKind(kind));
}

export function isSeriesStoreKind(kind) {
  return SERIES_STORE_KINDS.has(offerKind(kind));
}

export function seriesStoreKind(kind) {
  const offer = offerKind(kind);
  if (offer === 'people') return 'people';
  if (offer === 'custom') return 'custom';
  return 'world';
}

export function singletonDefaultTitle(kind) {
  const offer = offerKind(kind);
  return SINGLETON_DEFAULTS[offer] || LABELED_DEFAULTS[offer] || '';
}

/**
 * The card's own title: the character or place name already on the offer.
 * A draft edit wins, including a cleared name. No stand-in label when the
 * data has none — #1450 keeps new writer-facing words off this surface.
 */
export function offerCardTitle(candidate, draft) {
  const raw = draft && 'title' in draft
    ? draft.title
    : (candidate?.title || candidate?.name || '');
  return String(raw ?? '').trim();
}

export function docsCharacterOffersPath({ projectId, storyId } = {}) {
  if (!projectId) return '/projects';
  if (storyId) return `/projects/${projectId}/stories/${storyId}/docs-offers`;
  return `/projects/${projectId}/docs-offers`;
}

export { isBookDocsOffersPath, isDocsOffersPath, isProjectDocsOffersPath };

export function docsCharacterOffersPathWithBatch(opts, batchId) {
  const base = docsCharacterOffersPath(opts);
  if (!batchId) return base;
  return `${base}?batch=${encodeURIComponent(batchId)}`;
}

export function doorAltitude({ storyId, isSeries } = {}) {
  if (storyId) return 'book';
  return isSeries ? 'series' : 'book';
}

export function destinationNote(altitude, kind = 'people') {
  const offer = offerKind(kind);
  if (offer === 'place') {
    return altitude === 'series' ? ACCEPT_PLACE_NOTE_SERIES : ACCEPT_PLACE_NOTE_BOOK;
  }
  if (offer === 'world') {
    return altitude === 'series' ? ACCEPT_WORLD_NOTE_SERIES : ACCEPT_WORLD_NOTE_BOOK;
  }
  if (offer === 'logline') return ACCEPT_LOGLINE_NOTE_BOOK;
  if (offer === 'theme') return ACCEPT_THEME_NOTE_BOOK;
  if (offer === 'conflict') return ACCEPT_CONFLICT_NOTE_BOOK;
  if (offer === 'relationship') return ACCEPT_RELATIONSHIP_NOTE_BOOK;
  if (offer === 'subplot') return ACCEPT_SUBPLOT_NOTE_BOOK;
  if (offer === 'rule') return ACCEPT_STORY_RULE_BOOK;
  if (offer === 'voice') return ACCEPT_VOICE_STYLE_BOOK;
  if (offer === 'custom') {
    return altitude === 'series' ? ACCEPT_CUSTOM_NOTE_SERIES : ACCEPT_CUSTOM_NOTE_BOOK;
  }
  return altitude === 'series' ? ACCEPT_CHARACTER_NOTE_SERIES : ACCEPT_CHARACTER_NOTE_BOOK;
}

const LOOKS_LIKE_BY_KIND = {
  people: LOOKS_LIKE_EXISTING_NOTE,
  place: LOOKS_LIKE_EXISTING_PLACE_NOTE,
  world: LOOKS_LIKE_EXISTING_WORLD_NOTE,
  logline: LOOKS_LIKE_EXISTING_LOGLINE_NOTE,
  theme: LOOKS_LIKE_EXISTING_THEME_NOTE,
  conflict: LOOKS_LIKE_EXISTING_CONFLICT_NOTE,
  relationship: LOOKS_LIKE_EXISTING_RELATIONSHIP_NOTE,
  subplot: LOOKS_LIKE_EXISTING_SUBPLOT_NOTE,
  custom: LOOKS_LIKE_EXISTING_CUSTOM_NOTE,
  rule: LOOKS_LIKE_EXISTING_STORY_RULE,
  voice: LOOKS_LIKE_EXISTING_VOICE,
};

const EXISTING_BY_KIND = {
  people: EXISTING_CHARACTER_NOTES,
  place: EXISTING_PLACE_NOTES,
  world: EXISTING_WORLD_NOTES,
  rule: EXISTING_STORY_RULES,
  voice: EXISTING_VOICES,
};

export function existingNotesCopy(kind, collision) {
  const offer = offerKind(kind);
  if (collision) return LOOKS_LIKE_BY_KIND[offer] || LOOKS_LIKE_EXISTING_NOTE;
  return EXISTING_BY_KIND[offer] || EXISTING_CHARACTER_NOTES;
}

export function emptyReasonCopy(reason) {
  if (reason === 'empty_docs') return EMPTY_DOCS_FOR_NOTES;
  if (reason === 'no_match') return NO_MATCH_IN_DOCS;
  if (reason === 'no_people') return NO_PEOPLE_IN_DOCS;
  return '';
}

export function groupCandidates(candidates) {
  const groups = {
    people: [], place: [], world: [],
    logline: [], theme: [], conflict: [], relationship: [], subplot: [], custom: [],
    rule: [], voice: [],
  };
  (candidates || []).forEach((candidate) => {
    const kind = offerKind(candidate.kind);
    if (groups[kind]) groups[kind].push({ ...candidate, kind });
  });
  return groups;
}

export function buildDocsChecklist(batches) {
  return (batches || []).map((batch) => {
    const files = (batch.documents || [])
      .map((doc) => doc.original_filename || doc.filename)
      .filter(Boolean);
    return {
      id: batch.batch_id || batch.id,
      kind: 'doc',
      label: files.join(', ') || 'Uploaded docs',
      detail: docListDetail(batch, 'In the Docs pile.'),
      scopeNote: batch.scope_note || '',
    };
  }).filter((item) => item.id);
}

export async function loadDocsOfferSources(projectId, {
  listImports = (id) => documentImportApi.list(id),
} = {}) {
  try {
    const batches = unwrapImportBatches(await listImports(projectId));
    return { docs: buildDocsChecklist(batches), error: null };
  } catch (err) {
    return { docs: [], error: err };
  }
}

export function selectedIds(checked, items) {
  return (items || []).filter((item) => checked[item.id]).map((item) => item.id);
}

export function defaultDisposition(candidate) {
  return candidate?.collision ? 'update' : 'new';
}

export function pickExistingTarget(candidate, disposition) {
  if (disposition !== 'update') return null;
  const collisionId = candidate?.collision?.existing_id;
  const existing = candidate?.existing || [];
  return existing.find((row) => String(row.id) === String(collisionId)) || existing[0] || null;
}

export function needsAltitudePicker(candidate, { isSeries, doorAltitude: door } = {}) {
  if (!isSeries) return false;
  if (isBookOnlyKind(candidate?.kind)) return false;
  if (candidate?.altitude_ambiguous) return true;
  if (candidate?.suggested_altitude && candidate.suggested_altitude !== door) return true;
  const stores = new Set((candidate?.existing || []).map((row) => row.store).filter(Boolean));
  return stores.size > 1;
}

export function resolveAcceptAltitude(candidate, chosen, { isSeries, doorAltitude: door } = {}) {
  if (!isSeries) return 'book';
  if (chosen === 'book' || chosen === 'series') return chosen;
  return candidate?.suggested_altitude || door || 'series';
}

function missingFieldsError(kind) {
  const offer = offerKind(kind);
  if (offer === 'place') return 'Give this Place note a name and what the place is before you accept it.';
  if (offer === 'world') return 'Give this World note its facts before you accept it.';
  if (offer === 'logline') return 'Give this Logline note its text before you accept it.';
  if (offer === 'theme') return 'Give this Theme note its text before you accept it.';
  if (offer === 'conflict') return 'Give this Conflict note its text before you accept it.';
  if (offer === 'relationship') return 'Give this Relationship note a name and what it is before you accept it.';
  if (offer === 'subplot') return 'Give this Subplot note a name and what it is before you accept it.';
  if (offer === 'custom') return 'Give this Custom note a title and its text before you accept it.';
  if (offer === 'rule') return 'Give this Story Rule the hard rule that must stay true before you accept it.';
  if (offer === 'voice') return GIVE_VOICE_ITS_TEXT;
  return 'Give this Character note a name and a portrait before you accept it.';
}

export async function persistAcceptedDocsNote({
  projectId,
  storyId,
  altitude,
  disposition,
  candidate,
  kind,
  title,
  statement,
  existingTarget,
  addCards = (id, cards) => storyBibleApi.addCards(id, cards),
  updateEntry = (sid, eid, data) => storyBibleApi.updateEntry(sid, eid, data),
  getSnapshots = (id) => seriesSnapshotsApi.get(id),
  putSnapshots = (id, store) => seriesSnapshotsApi.put(id, store),
  addRule = (id, body) => storyRulesApi.add(id, body),
  updateRule = (sid, rid, body) => storyRulesApi.update(sid, rid, body),
  saveInstruction = (id, body) => styleCardsApi.saveInstruction(id, body),
} = {}) {
  const offer = offerKind(kind || candidate?.kind);
  const portrait = (statement || '').trim();
  let name = (title || '').trim();
  const defaultTitle = singletonDefaultTitle(offer);
  if (defaultTitle && (!name || name.toLowerCase() === `the ${defaultTitle.toLowerCase()}`)) {
    name = defaultTitle;
  }
  if (!portrait || (!defaultTitle && !name)) {
    throw new Error(missingFieldsError(offer));
  }
  if (offer === 'rule') {
    const bookId = existingTarget?.story_id || storyId;
    if (!bookId) {
      throw new Error(NO_BOOK_FOR_CHARACTER_NOTE);
    }
    if (disposition === 'update' && existingTarget?.store === 'rules' && existingTarget.id) {
      const updated = await updateRule(bookId, existingTarget.id, { sentence: portrait });
      return { store: 'rules', id: existingTarget.id || updated?.id || null, kind: 'rule' };
    }
    const created = await addRule(bookId, { sentence: portrait, scope_kind: 'book' });
    return { store: 'rules', id: created?.id || null, kind: 'rule' };
  }
  if (offer === 'voice') {
    const bookId = existingTarget?.story_id || storyId;
    if (!bookId) {
      throw new Error(NO_BOOK_FOR_CHARACTER_NOTE);
    }
    const cardId = disposition === 'update' && existingTarget?.store === 'style'
      ? existingTarget.id
      : null;
    const saved = await saveInstruction(bookId, {
      name: name || defaultTitle || 'Style',
      resolved_text: portrait,
      card_id: cardId,
    });
    return {
      store: 'style',
      id: saved?.id || null,
      kind: 'voice',
      status: saved?.status || 'draft',
      is_book_default: Boolean(saved?.is_book_default),
      applied: false,
    };
  }
  if (altitude === 'series' && isSeriesStoreKind(offer)) {
    const store = await getSnapshots(projectId);
    const target = existingTarget?.store === 'snapshots' ? existingTarget : null;
    const card = {
      id: disposition === 'update' && target?.id ? target.id : newCardId(),
      kind: seriesStoreKind(offer),
      name: name || defaultTitle || 'World',
      description: portrait,
      writeup: disposition === 'update' ? (target?.writeup || '') : '',
    };
    await putSnapshots(projectId, upsertCard(store, card));
    return { store: 'snapshots', id: card.id, kind: offer };
  }
  const bookId = existingTarget?.story_id || storyId;
  if (!bookId) {
    throw new Error(NO_BOOK_FOR_CHARACTER_NOTE);
  }
  const dest = BOOK_DEST[offer] || BOOK_DEST.people;
  const persistedName = name || dest.defaultTitle;
  const category = dest.category || name || 'Custom';
  if (disposition === 'update' && existingTarget?.store === 'book' && existingTarget.id) {
    await updateEntry(bookId, existingTarget.id, {
      name: persistedName,
      description: portrait,
      instance_name: dest.instanceName ? name : undefined,
    });
    return { store: 'book', id: existingTarget.id, kind: offer };
  }
  const created = await addCards(bookId, [{
    name: persistedName,
    description: portrait,
    category,
    tier: dest.tier,
    instance_name: dest.instanceName ? name : null,
  }]);
  return { store: 'book', id: created?.entries?.[0]?.id || null, kind: offer };
}

export async function persistAcceptedCharacterNote(opts = {}) {
  return persistAcceptedDocsNote({ ...opts, kind: 'people' });
}
